import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Sparkles, X } from 'lucide-react';

import { fetchConfiguracao } from '@/api/secretario';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useAuth } from '@/hooks/useAuth';
import { queryKeys } from '@/lib/pwa/queryConfig';

/**
 * Chamada de ativação no dashboard. Só aparece para advogado e enquanto o
 * Secretário não estiver ativo — uma vez ligado, some (vira só o item de menu).
 * Reusa a mesma query da página de configuração (cache compartilhado).
 *
 * Dispensável de vez (localStorage, por usuário): quem não quer o Secretário
 * não precisa ver o convite toda vez que abre o painel. O menu continua lá.
 */
export function SecretarioAtivacaoBanner() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdvogado = user?.role === 'advogado';
  const chaveDispensa = `secretario:bannerDispensado:${user?.id ?? ''}`;
  const [dispensadoAgora, setDispensadoAgora] = useState<string | null>(null);
  let dispensado = dispensadoAgora === chaveDispensa;
  try {
    dispensado ||= localStorage.getItem(chaveDispensa) === '1';
  } catch {
    /* localStorage indisponível — vale só o estado em memória */
  }

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.secretario.config,
    queryFn: fetchConfiguracao,
    enabled: isAdvogado && !dispensado,
  });

  const dispensar = () => {
    setDispensadoAgora(chaveDispensa);
    try {
      localStorage.setItem(chaveDispensa, '1');
    } catch {
      /* sem persistência — a dispensa vale até a página recarregar */
    }
  };

  // Não pisca enquanto carrega; some quando já está ativo ou foi dispensado.
  // Quem só participa do Secretário do escritório não o ativa: o aviso não é para ele.
  if (!isAdvogado || dispensado || isLoading || data?.ativo || data?.papel === 'PARTICIPANTE') return null;

  const semConfig = !data;
  const titulo = semConfig ? 'Ative seu Secretário de IA' : 'Falta pouco para ativar seu Secretário de IA';
  const descricao = semConfig
    ? 'Deixe a IA atender seus clientes pelo WhatsApp: triagem, documentos e contratos automáticos.'
    : 'Complete o checklist de prontidão para começar a atender automaticamente.';

  return (
    <Card className="relative border-primary/20 bg-primary/5">
      <Button
        variant="ghost"
        size="icon"
        className="absolute right-1 top-1 h-11 w-11 text-muted-foreground hover:bg-transparent hover:text-foreground sm:h-8 sm:w-8"
        onClick={dispensar}
        aria-label="Dispensar aviso do Secretário de IA"
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </Button>
      {/* No celular, uma linha (ícone, título e o botão): com a descrição e o botão largo, o
          convite ocupava meia tela do Painel (docs/PLANO_RESPONSIVIDADE_MOBILE.md §3.7). */}
      <CardContent className="flex items-center gap-3 p-3 pr-12 sm:justify-between sm:gap-4 sm:p-6 sm:pr-12">
        <div className="flex min-w-0 flex-1 items-center gap-3 sm:items-start sm:gap-4">
          <div className="shrink-0 rounded-lg bg-primary/10 p-2 sm:p-2.5">
            <Sparkles className="h-5 w-5 text-primary sm:h-6 sm:w-6" />
          </div>
          <div className="min-w-0">
            <h3 className="text-sm font-semibold sm:text-base">{titulo}</h3>
            <p className="hidden text-sm text-muted-foreground sm:block">{descricao}</p>
          </div>
        </div>
        <Button onClick={() => navigate('/app/secretario-ia')} className="h-11 shrink-0 sm:h-9">
          {semConfig ? 'Configurar' : 'Continuar configuração'}
        </Button>
      </CardContent>
    </Card>
  );
}
